import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createAction, partyActionKeys } from '@/services/partyActions'
import { actionPointKeys } from '@/services/actionPoints'
import type { CreatePartyActionInput, PartyAction } from '@/types'

interface UseCreateActionOptions {
  onSuccess?: (data: PartyAction) => void
  onError?: (error: Error) => void
}

export function useCreateAction(options?: UseCreateActionOptions) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (input: CreatePartyActionInput) => createAction(input),
    onSuccess: (data) => {
      // Invalidate queued actions to refresh the list
      queryClient.invalidateQueries({
        queryKey: partyActionKeys.queued(data.party_id, data.tick),
      })

      // Invalidate action points to reflect spent AP
      queryClient.invalidateQueries({
        queryKey: actionPointKeys.byPartyTick(data.party_id, data.tick),
      })

      options?.onSuccess?.(data)
    },
    onError: (error: Error) => {
      options?.onError?.(error)
    },
  })

  return {
    createAction: mutation.mutate,
    createActionAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  }
}
